import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { TypeDataGridProps, TypeSortInfo } from '@inovua/reactdatagrid-enterprise/types';
import AssetManagerPage from 'components/common/AssetManagerPage';
import Layout from 'components/common/Layout';
import { useFetchConfigTotals, useFetchConfigRequests, useToast } from 'hooks';
import ConfigRequestsTable from 'components/analytics/ConfigRequestsTable';
import ConfigTotalsTable from 'components/analytics/ConfigTotalsTable';
import { checkIfError } from 'utils/helpers';
import { mapFilterBy, mapSortInfo } from 'utils/dataTable';
import { Box, Flex } from '@chakra-ui/react';
import { Heading, SimpleGrid } from 'components/UI';
import { Pages } from 'constants/pages';
import { IConfigurationRequestTotal } from '../../../server/types';

const Analytics = () => {
  const [paginationLoading, setPaginationLoading] = useState(true);
  const [configTotalTableData, setConfigTotalTableData] = useState<IConfigurationRequestTotal[]>([]);
  const [, setSortInfo] = useState([]);
  const [configRequestTotal, setConfigRequestTotal] = useState(0);

  const [showToast] = useToast();

  const [fetchConfigTotals, { error: fetchConfigTotalsError, loading: fetchConfigTotalsLoading }] =
    useFetchConfigTotals();

  const [fetchConfigRequests] = useFetchConfigRequests();

  const handleLoadConfigTotals = async () => {
    const configTotalsResult = await fetchConfigTotals();

    if (checkIfError(configTotalsResult)) return;

    setConfigTotalTableData(configTotalsResult);
  };

  useEffect(() => {
    const message = fetchConfigTotalsError;

    if (!message) return;

    showToast({
      message,
      status: 'error',
    });
  }, [fetchConfigTotalsError]);

  useEffect(() => {
    handleLoadConfigTotals();
  }, []);

  const handleLoadMoreConfigurations = async ({ skip, limit, sortInfo, filterValue }: TypeDataGridProps) => {
    setPaginationLoading(true);

    const { configRequests, total } = await fetchConfigRequests({
      skip,
      limit,
      sort: JSON.stringify(mapSortInfo(sortInfo)),
      filter: JSON.stringify(mapFilterBy(filterValue)),
    });

    setPaginationLoading(false);
    setConfigRequestTotal(total);
    return { data: configRequests, count: total };
  };

  return (
    <Layout testId="analytics-page" containerProps={{ sx: { overflowX: 'visible' } }} pages={[Pages.Analytics]}>
      <Helmet title="Analytics" />
      <AssetManagerPage title="Analytics">
        <Flex justifyContent="center" flexWrap="wrap" w="full">
          <Flex justifyContent="center" w="full">
            <SimpleGrid columns={4} w="90%">
              <Box>
                <Heading
                  sx={{
                    letterSpacing: 'wide',
                    fontSize: '3xl',
                    textAlign: 'center',
                    fontWeight: 'medium',
                    color: 'gray.600',
                    my: 8,
                  }}
                  as="h3"
                >
                  Total Designs Generated
                </Heading>
                <ConfigTotalsTable rows={configTotalTableData} loading={fetchConfigTotalsLoading} />
              </Box>
              <Box gridColumn="span 3">
                <Heading
                  as="h3"
                  sx={{
                    letterSpacing: 'wide',
                    fontSize: '3xl',
                    textAlign: 'center',
                    fontWeight: 'medium',
                    color: 'gray.600',
                    my: 8,
                  }}
                >
                  Generated Design Details
                </Heading>
                <ConfigRequestsTable
                  total={configRequestTotal}
                  loading={paginationLoading}
                  onLoadMore={handleLoadMoreConfigurations}
                  onLoadingChange={setPaginationLoading}
                  onSortInfoChange={setSortInfo as (sortInfo: TypeSortInfo) => void}
                />
              </Box>
            </SimpleGrid>
          </Flex>
        </Flex>
      </AssetManagerPage>
    </Layout>
  );
};

export default Analytics;
